/* ==========================================================================
   FIXTURESS — assistant

   A guided assistant over the real catalogue. Deliberately not a language
   model: a Shopify theme runs in the browser, so an LLM-backed bot would ship
   its API key to every visitor. This reads store data injected by Liquid and
   hands off to a person the moment it would otherwise have to invent.

   The rule the whole file is built around: NEVER state an unpublished fact.
   Shipping times, return windows, dimensions, IP ratings and warranty are all
   unknown today. Each has an explicit handler that says so and offers the
   email — none of them fall through to a guess. A delivery estimate invented
   in a chat window is a promise the business then has to keep.
   ========================================================================== */

(function () {
  'use strict';

  var dataEl = document.getElementById('ChatData');
  var panel  = document.querySelector('[data-chat-panel]');
  var launch = document.querySelector('[data-chat-open]');
  if (!dataEl || !panel || !launch) return;

  var DB;
  try { DB = JSON.parse(dataEl.textContent); } catch (e) { return; }

  var log     = panel.querySelector('[data-chat-log]');
  var replies = panel.querySelector('[data-chat-replies]');
  var form    = panel.querySelector('[data-chat-form]');
  var input   = panel.querySelector('[data-chat-input]');
  var scrim   = document.querySelector('[data-chat-scrim]');
  var closeBtn= panel.querySelector('[data-chat-close]');

  var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var lastFocus = null;
  var started = false;

  /* ------------------------------------------------------------- render -- */

  function el(tag, cls, text) {
    var n = document.createElement(tag);
    if (cls) n.className = cls;
    if (text != null) n.textContent = text;
    return n;
  }

  function scrollDown() { log.scrollTop = log.scrollHeight; }

  function say(who, nodes) {
    var row = el('div', 'chat__msg chat__msg--' + who);
    (Array.isArray(nodes) ? nodes : [nodes]).forEach(function (n) {
      row.appendChild(typeof n === 'string' ? el('p', null, n) : n);
    });
    log.appendChild(row);
    scrollDown();
    return row;
  }

  /* Bot lines land with a short beat between them so a three-part answer
     reads as speech rather than arriving as a wall. Instant under reduced
     motion — the delay is presentational, not informational. */
  function botSay(nodes, delay) {
    if (reduce) { say('bot', nodes); return Promise.resolve(); }
    return new Promise(function (res) {
      setTimeout(function () { say('bot', nodes); res(); }, delay == null ? 260 : delay);
    });
  }

  function productList(items) {
    var ul = el('ul', 'chat__products');
    items.forEach(function (p) {
      var li  = el('li');
      var a   = el('a', 'chat__product');
      a.href  = p.url;
      a.appendChild(el('span', 'chat__product-name', p.title));
      var meta = (p.from ? 'From ' : '') + p.price + (p.available ? '' : ' · made to order');
      a.appendChild(el('span', 'chat__product-price', meta));
      li.appendChild(a);
      ul.appendChild(li);
    });
    return ul;
  }

  function link(href, text, cls) {
    var a = el('a', cls || 'chat__link', text);
    a.href = href;
    return a;
  }

  function mailLink(subject) {
    return link('mailto:' + DB.email + (subject ? '?subject=' + encodeURIComponent(subject) : ''), DB.email);
  }

  function setReplies(list) {
    replies.textContent = '';
    list.forEach(function (r) {
      var b = el('button', 'chat__chip', r.label);
      b.type = 'button';
      b.addEventListener('click', function () { ask(r.send || r.label); });
      replies.appendChild(b);
    });
  }

  var DEFAULT_CHIPS = [
    { label: 'Help me choose' },
    { label: 'Prices' },
    { label: 'Colour temperature' },
    { label: 'Trade enquiry' }
  ];

  /* ------------------------------------------------------------ matching -- */

  /* Normalised text is space-padded on both ends so a whole-word test is just
     a substring test with spaces either side. */
  function norm(s) {
    return (' ' + s + ' ').toLowerCase().replace(/[^a-z0-9 ]/g, ' ').replace(/\s+/g, ' ');
  }

  /* Whole words, both ends. Anchoring only the start matched "dim" inside
     "dimensions", which sent every specification question to the colour
     temperature answer — confidently, and about the wrong thing. Multi-word
     phrases still work because the padding is applied to the phrase too. */
  function has(t, words) {
    return words.some(function (w) { return t.indexOf(' ' + w + ' ') > -1; });
  }

  var ROOMS = [
    { key: 'kitchen', words: ['kitchen', 'island', 'bench'] },
    { key: 'dining',  words: ['dining', 'dinner', 'table'] },
    { key: 'hallway', words: ['hallway', 'hall', 'corridor', 'passage'] },
    { key: 'bedroom', words: ['bedroom', 'bed'] },
    { key: 'stairs',  words: ['stair', 'stairs', 'stairwell', 'landing', 'void'] },
    { key: 'entry',   words: ['entry', 'entrance', 'foyer', 'porch'] },
    { key: 'living',  words: ['living', 'lounge', 'family room'] }
  ];

  function byRoom(key) {
    return DB.products.filter(function (p) { return (p.rooms || []).indexOf(key) > -1; });
  }

  function cheapestFirst(a) {
    return a.slice().sort(function (x, y) { return x.priceCents - y.priceCents; });
  }

  /* ------------------------------------------------------------ handlers -- */

  function handoff(subject) {
    var p = el('p');
    p.appendChild(document.createTextNode('Email '));
    p.appendChild(mailLink(subject));
    p.appendChild(document.createTextNode(' and a person will answer, or use the '));
    p.appendChild(link(DB.links.contact, 'contact form'));
    p.appendChild(document.createTextNode('.'));
    return p;
  }

  var INTENTS = [
    {
      id: 'greet',
      priority: 1,
      test: function (t) { return has(t, ['hi', 'hello', 'hey', 'good morning', 'good afternoon']); },
      run: function () {
        botSay('Hello. I can help you find a fitting or answer questions about the range.');
        setReplies(DEFAULT_CHIPS);
      }
    },
    {
      id: 'choose',
      priority: 5,
      test: function (t) { return has(t, ['choose', 'recommend', 'suggest', 'which', 'help me', 'looking for', 'need a', 'best for']); },
      run: function () {
        botSay('Tell me the room and I will narrow it down.');
        setReplies([
          { label: 'Kitchen island', send: 'kitchen' },
          { label: 'Dining table', send: 'dining' },
          { label: 'Hallway', send: 'hallway' },
          { label: 'Bedroom', send: 'bedroom' },
          { label: 'Stairwell', send: 'stairwell' },
          { label: 'Entry', send: 'entry' }
        ]);
      }
    },
    {
      id: 'room',
      priority: 9,
      test: function (t) { return ROOMS.some(function (r) { return has(t, r.words); }); },
      run: function (t) {
        var room = ROOMS.find(function (r) { return has(t, r.words); });
        var hits = cheapestFirst(byRoom(room.key));
        if (!hits.length) {
          botSay('Nothing in the range is written up specifically for that room, so I would rather not guess.')
            .then(function () { say('bot', handoff('Which fitting for my room?')); });
          setReplies(DEFAULT_CHIPS);
          return;
        }
        botSay('For a ' + room.key + ', these are the fittings designed for it:')
          .then(function () {
            say('bot', productList(hits));
            return botSay('All of them switch between 3000K, 4000K and 6000K.', 220);
          });
        setReplies([
          { label: 'See the whole range', send: 'show me everything' },
          { label: 'Colour temperature' },
          { label: 'Finishes' }
        ]);
      }
    },
    {
      id: 'price',
      priority: 3,
      test: function (t) { return has(t, ['price', 'prices', 'cost', 'how much', 'pricing', 'cheap', 'expensive', 'budget']); },
      run: function () {
        botSay('Here is the range, lowest first.')
          .then(function () {
            say('bot', productList(cheapestFirst(DB.products)));
            return botSay('Prices show in your local currency — the switcher is in the header.', 220);
          });
        setReplies([{ label: 'Help me choose' }, { label: 'Trade enquiry' }]);
      }
    },
    {
      id: 'range',
      priority: 4,
      test: function (t) { return has(t, ['everything', 'all', 'range', 'catalogue', 'catalog', 'what do you sell', 'products']); },
      run: function () {
        botSay('The whole range — seven fittings.')
          .then(function () { say('bot', productList(cheapestFirst(DB.products))); });
        setReplies([{ label: 'Help me choose' }, { label: 'Prices' }]);
      }
    },
    {
      id: 'temp',
      priority: 6,
      test: function (t) { return has(t, ['colour temperature', 'color temperature', 'temperature', 'kelvin', '3000k', '4000k', '6000k', 'warm', 'cool', 'dimmable', 'dimmer', 'dimming']); },
      run: function () {
        botSay('Every fitting switches between three colour temperatures: 3000K for evenings, 4000K for a working kitchen, and 6000K where the task demands it. One fitting, adjusted to the hour rather than replaced.')
          .then(function () {
            var p = el('p', null, 'The whole site does the same thing — the three dots in the header shift it. Want me to show you?');
            say('bot', p);
          });
        setReplies([
          { label: 'Show me 3000K', send: '__temp_warm' },
          { label: 'Show me 6000K', send: '__temp_cool' },
          { label: 'Back to neutral', send: '__temp_neutral' },
          { label: 'Help me choose' }
        ]);
      }
    },
    {
      id: 'finish',
      priority: 7,
      test: function (t) { return has(t, ['finish', 'finishes', 'colour', 'color', 'brass', 'black', 'white', 'gold']); },
      run: function () {
        var withFinishes = DB.products.filter(function (p) { return (p.finishes || []).length; });
        var lines = withFinishes.map(function (p) { return p.title + ' — ' + p.finishes.join(' or '); });
        botSay('The range is kept to a restrained palette so the fittings sit with the materials already in the room.')
          .then(function () {
            if (lines.length) {
              var ul = el('ul', 'chat__plain');
              lines.forEach(function (l) { ul.appendChild(el('li', null, l)); });
              say('bot', ul);
            }
            return botSay('The rest are matte black. If you need a finish that is not listed, ask — I will not guess at what is available.', 220);
          });
        setReplies([{ label: 'Help me choose' }, { label: 'Prices' }]);
      }
    },
    {
      id: 'specs',
      priority: 8,
      test: function (t) { return has(t, ['dimension', 'dimensions', 'size', 'sizes', 'diameter', 'width', 'height', 'drop', 'ip rating', 'ip', 'warranty', 'wattage', 'lumens', 'weight', 'spec', 'specs', 'specification']); },
      run: function () {
        botSay('Dimensions, IP ratings and warranty are not published yet, and I will not guess at them — a wrong measurement is worse than no measurement.')
          .then(function () { say('bot', handoff('Specifications request')); });
        setReplies([{ label: 'Trade enquiry' }, { label: 'Help me choose' }]);
      }
    },
    {
      id: 'shipping',
      priority: 8,
      test: function (t) { return has(t, ['ship', 'shipping', 'delivery', 'deliver', 'postage', 'freight', 'how long', 'lead time', 'dispatch', 'track', 'tracking']); },
      run: function () {
        var published = !!DB.policies.shipping;
        if (published) {
          var p = el('p');
          p.appendChild(document.createTextNode('The details are in our '));
          p.appendChild(link(DB.policies.shipping, 'shipping policy'));
          p.appendChild(document.createTextNode('.'));
          botSay(p);
        } else {
          botSay('Shipping times and costs are not published yet, so I would only be guessing — and a delivery date I invent is one the studio then has to meet.')
            .then(function () { say('bot', handoff('Shipping question')); });
        }
        setReplies([{ label: 'Help me choose' }, { label: 'Prices' }]);
      }
    },
    {
      id: 'returns',
      priority: 8,
      test: function (t) { return has(t, ['return', 'returns', 'refund', 'exchange', 'send it back', 'change my mind', 'warranty claim', 'faulty', 'damaged']); },
      run: function () {
        if (DB.policies.refund) {
          var p = el('p');
          p.appendChild(document.createTextNode('See our '));
          p.appendChild(link(DB.policies.refund, 'returns policy'));
          p.appendChild(document.createTextNode('.'));
          botSay(p);
        } else {
          botSay('The returns policy is not published yet, so I will not state terms I cannot stand behind.')
            .then(function () { say('bot', handoff('Returns question')); });
        }
        setReplies([{ label: 'Contact' }]);
      }
    },
    {
      id: 'trade',
      priority: 8,
      test: function (t) { return has(t, ['trade', 'project', 'projects', 'bulk', 'quantity', 'wholesale', 'builder', 'architect', 'designer', 'specify', 'spec sheet', 'developer', 'commercial']); },
      run: function () {
        var p = el('p');
        p.appendChild(document.createTextNode('We work with builders, architects, interior designers and developers. Project pricing is available on quantity, with specification support and staged delivery. Details on the '));
        p.appendChild(link(DB.links.trade, 'trade page'));
        p.appendChild(document.createTextNode('.'));
        botSay(p).then(function () { say('bot', handoff('Trade enquiry')); });
        setReplies([{ label: 'Prices' }, { label: 'Help me choose' }]);
      }
    },
    {
      id: 'contact',
      test: function (t) { return has(t, ['contact', 'email', 'phone', 'call', 'speak', 'human', 'person', 'someone', 'talk to']); },
      run: function () {
        botSay('A person answers everything here.').then(function () { say('bot', handoff('Enquiry')); });
        setReplies([{ label: 'Trade enquiry' }, { label: 'Help me choose' }]);
      }
    },
    {
      id: 'stock',
      priority: 7,
      test: function (t) { return has(t, ['stock', 'in stock', 'available', 'availability', 'back order', 'backorder', 'sold out']); },
      run: function () {
        var out = DB.products.filter(function (p) { return !p.available; });
        if (!out.length) {
          botSay('Everything in the range is currently orderable.')
            .then(function () { say('bot', productList(cheapestFirst(DB.products))); });
        } else {
          botSay('These are made to order rather than held in stock:')
            .then(function () {
              say('bot', productList(out));
              return botSay('Lead times are not published, so ask and a person will give you a real date.', 220);
            });
        }
        setReplies([{ label: 'Contact' }, { label: 'Help me choose' }]);
      }
    }
  ];

  /* The dimmer demo drives the header control rather than setting the token
     itself, so persistence and the buttons' aria-pressed state stay correct
     instead of silently drifting out of sync with the page. */
  function demoTemp(which) {
    var btn = document.querySelector('.hdr [data-temp-set="' + which + '"]') ||
              document.querySelector('[data-temp-set="' + which + '"]');
    if (btn) btn.click();
    else document.documentElement.dataset.temp = which;
    var names = { warm: '3000K — evening', neutral: '4000K — neutral', cool: '6000K — task' };
    botSay('There it is: ' + names[which] + '. The three dots in the header change it any time.');
    setReplies([
      { label: 'Show me 3000K', send: '__temp_warm' },
      { label: 'Show me 6000K', send: '__temp_cool' },
      { label: 'Back to neutral', send: '__temp_neutral' },
      { label: 'Help me choose' }
    ]);
  }

  function fallback() {
    botSay('I did not follow that one, and I would rather hand you to a person than guess.')
      .then(function () { say('bot', handoff('Question from the website')); });
    setReplies(DEFAULT_CHIPS);
  }

  function ask(raw) {
    var text = String(raw || '').trim();
    if (!text) return;

    if (text.indexOf('__temp_') === 0) {
      say('you', text.replace('__temp_', 'Show me ').replace('warm', '3000K').replace('cool', '6000K').replace('neutral', 'neutral'));
      demoTemp(text.replace('__temp_', ''));
      return;
    }

    say('you', text);
    replies.textContent = '';
    var t = norm(text);

    /* Highest specificity wins, not first in the list. "Do you do trade
       pricing" matches both `trade` and `price`; picking whichever happened to
       be declared first answered a trade enquiry with a price list. Every
       intent carries a priority, and the most specific match takes it. */
    var hit = INTENTS
      .filter(function (i) { return i.test(t); })
      .sort(function (a, b) { return (b.priority || 0) - (a.priority || 0); })[0];

    if (hit) hit.run(t); else fallback();
  }

  /* --------------------------------------------------------- open/close -- */

  function focusables() {
    return Array.prototype.slice.call(
      panel.querySelectorAll('a[href], button:not([disabled]), input, [tabindex]:not([tabindex="-1"])')
    ).filter(function (n) { return n.offsetParent !== null; });
  }

  function onKey(e) {
    if (e.key === 'Escape') { close(); return; }
    if (e.key !== 'Tab') return;
    var f = focusables();
    if (!f.length) return;
    var first = f[0], last = f[f.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  }

  function open() {
    lastFocus = document.activeElement;
    panel.removeAttribute('inert');
    panel.classList.add('is-open');
    launch.setAttribute('aria-expanded', 'true');
    if (scrim) scrim.hidden = false;
    document.addEventListener('keydown', onKey);

    if (!started) {
      started = true;
      say('bot', 'Hello. I answer from the range — prices, finishes, what suits which room. Anything I cannot answer honestly, I hand to a person.');
      setReplies(DEFAULT_CHIPS);
    }
    // Focus the input, not the panel, so a keyboard user can just start typing.
    setTimeout(function () { input.focus(); }, reduce ? 0 : 260);
  }

  function close() {
    panel.classList.remove('is-open');
    panel.setAttribute('inert', '');
    launch.setAttribute('aria-expanded', 'false');
    if (scrim) scrim.hidden = true;
    document.removeEventListener('keydown', onKey);
    // Fall back to the launcher: if the panel was opened without the button
    // ever taking focus, lastFocus is <body> and focus would be lost.
    var back = (lastFocus && lastFocus.focus && lastFocus !== document.body) ? lastFocus : launch;
    back.focus();
  }

  launch.addEventListener('click', function () {
    if (launch.getAttribute('aria-expanded') === 'true') close(); else open();
  });
  if (closeBtn) closeBtn.addEventListener('click', close);
  if (scrim) scrim.addEventListener('click', close);

  form.addEventListener('submit', function (e) {
    e.preventDefault();
    var v = input.value;
    input.value = '';
    ask(v);
  });
}());
